import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BaseService } from 'src/app/services/base.service';
import { environment } from '../../../../environments/environment';
import { AuthSupportPagedList } from '../../../../swagger-gen/web-secured/models/auth-support-paged-list';
import { AuthSupportsStateChange } from '../../../../swagger-gen/web-secured/models/auth-supports-state-change';
import { TransactionPagedList } from '../../../../swagger-gen/web-secured/models/transaction-paged-list';
import { Badge } from '../../../models/badge/badge';
import { BadgePageable } from '../../../models/badge/badge-pageable';
import { BadgeState } from '../../../models/badge/badge-state';
import { TransactionPageable } from '../../../models/transaction/transactionResponseList';
import { CustomerAccountTransactionRepoModelMapper } from '../../customer-account-transaction/mappers/model/customer-account-transaction-repo.model.mapper';
import { BadgeRepoDtoMapper } from '../mappers/dto/badge-repo.dto.mapper';
import { BadgeRepoModelMapper, VnfAuthSupport } from '../mappers/model/badge-repo.model.mapper';

@Injectable({
    providedIn: 'root',
})
export class BadgeRepo extends BaseService {
    badgeUrl = environment.apiUrl + '/auth-supports';
    badgeUrlCustom = environment.apiUrl + '/custom/auth-supports';

    constructor(
        private readonly http: HttpClient,
        private readonly logger: NGXLogger
    ) {
        super();
    }

    getAllBadgeList(offset: number, limit: number, search: string): Observable<BadgePageable> {
        return this.getBadges(null, offset, limit, search);
    }

    getBadgeStockList(offset: number, limit: number, search: string): Observable<BadgePageable> {
        return this.getBadges(true, offset, limit, search);
    }

    private getBadges(stock: boolean | null, offset: number, limit: number, search: string): Observable<BadgePageable> {
        const url = `${this.badgeUrl}/search`;
        let params = new HttpParams()
        .set('offset', offset)
        .set('limit', limit)
        .set('search', search);
        if (stock !== null) {
            params = params.set('availableAuthSupport', stock);
        }

        return this.http.post<AuthSupportPagedList>(url, { 'filters': [] }, {
            headers: this.httpHeaders(),
            params: params
        }).pipe(
            tap((response) => {
                this.logger.log(`Get a customerAccount transaction list from backend`, response);
            }), // DEBUG
            map(BadgeRepoModelMapper.toList),
            catchError(this.handleServerError)
        );
    }

    getBadge(id: string): Observable<Badge> {
        const url = `${this.badgeUrlCustom}/${id}`;
        return this.http.get<VnfAuthSupport>(url, {
            headers: this.httpHeaders(),
        }).pipe(
            tap((response) => {
                this.logger.log(`Get a customerAccount from backend`, response);
            }), // DEBUG
            map(r => BadgeRepoModelMapper.toBadge(r)),
            catchError(this.handleServerError)
        );
    }

    createBadge(badge: Badge): Observable<Badge> {
        const badgeCreate = BadgeRepoDtoMapper.toCreate(badge);
        return this.http.post(this.badgeUrlCustom, badgeCreate, {
            headers: this.httpHeaders(),
            observe: 'response'
        }).pipe(
            tap(() => {
                this.logger.log(`Create a badge from backend`, badgeCreate);
            }), // DEBUG
            map((response) => {
                const id = response.headers.get('Uid');
                return Object.assign(badge, { id: id });
            }),
            catchError(this.handleServerError)
        );
    }

    updateBadge(badge: Badge): Observable<Badge> {
        const url = `${this.badgeUrlCustom}/${badge.id}`;
        const badgeUpdate = BadgeRepoDtoMapper.toUpdate(badge);
        return this.http.put(url, badgeUpdate, {
            headers: this.httpHeaders(),
        }).pipe(
            tap(() => {
                this.logger.log(`Update a badge from backend`, badgeUpdate);
            }), // DEBUG
            map(() => badge),
            catchError(this.handleServerError)
        );
    }

    updateState(badgeIdList: string[], state: BadgeState): Observable<void> {
        const url = `${this.badgeUrlCustom}/state`;
        const badgeStateUpdate: AuthSupportsStateChange = BadgeRepoDtoMapper.toUpdateState(badgeIdList, state);
        return this.http.post(url, badgeStateUpdate, {
            headers: this.httpHeaders(),
        }).pipe(
            tap(() => {
                this.logger.log(`Update a badge state from backend`, badgeStateUpdate);
            }), // DEBUG
            map(() => void 0),
            catchError(this.handleServerError)
        );
    }

    removeBadge(badgeId: string): Observable<void> {
        const url = `${this.badgeUrl}/${badgeId}`;
        return this.http.delete<void>(url, {
            headers: this.httpHeaders(),
        }).pipe(
            tap(() => {
                this.logger.log(`Remove a badge from backend`, badgeId);
            }), // DEBUG
            catchError(this.handleServerError)
        );
    }

    getTransactionList(badgeId: string, offset: number, limit: number, startDate: Date, endDate: Date): Observable<TransactionPageable> {
        const url = `${this.badgeUrl}/${badgeId}/transactions`;
        return this.http.get<TransactionPagedList>(url, {
            headers: this.httpHeaders(),
            params: new HttpParams()
                .set('offset', offset)
                .set('limit', limit)
                .set('startDateTime', startDate.toISOString())
                .set('endDateTime', endDate.toISOString())
        }).pipe(
            tap((response) => {
                this.logger.log(`Get a badge transaction list from backend`, response);
            }), // DEBUG
            map(CustomerAccountTransactionRepoModelMapper.toList),
            catchError(this.handleServerError)
        );
    }

    exportTransactionList(badgeId: string): Observable<HttpResponse<Blob>> {
        const url = `${this.badgeUrl}/${badgeId}/transactions/export`;
        return this.http.post<Blob>(
            url,
            {},
            { headers: this.httpHeaders(), responseType: 'blob' as 'json', observe: 'response' }
        ).pipe(
            catchError(this.handleServerError)
        );
    }

    export(): Observable<HttpResponse<Blob>> {
        const url = `${this.badgeUrl}/export`;
        return this.http.get<Blob>(url, { headers: this.httpHeaders(), responseType: 'blob' as 'json', observe: 'response' }).pipe(
            catchError(this.handleServerError)
        );
    }

    import(file: Blob): Observable<HttpResponse<void>> {
        const url = `${this.badgeUrl}/import`;
        return this.http.post<void>(url, file, { headers: this.httpHeadersForExcel(), observe: 'response' }).pipe(
            catchError(this.handleServerError)
        );
    }

}
